/**
 * HHMMInput — input that displays/edits time as HH:MM but stores total minutes.
 *
 * Props:
 *   value: number | null    — total minutes (source of truth)
 *   onChange: (mins) => void
 *   placeholder, disabled, "data-testid", className, max
 *
 * Behaviour:
 *   - Local string state ("HH:MM" or "H:MM") so user can type freely
 *   - On blur, parses + normalizes; calls onChange with integer minutes
 *   - Invalid input → onChange(null) and red border hint
 *   - Accepts "75" → 75 minutes → 1:15. Accepts "1:15" or "01:15".
 */
import { useEffect, useState } from "react";
import { Input } from "./input";


function minutesToHHMM(mins) {
  if (mins == null || mins === "" || Number.isNaN(Number(mins))) return "";
  const m = Math.max(0, Number(mins) | 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}:${String(r).padStart(2, "0")}`;
}

function hhmmToMinutes(str) {
  if (!str || !String(str).trim()) return null;
  const s = String(str).trim();
  if (s.includes(":")) {
    const [h, m = "0"] = s.split(":");
    const hi = parseInt(h, 10);
    const mi = parseInt(m, 10);
    if (Number.isNaN(hi) || Number.isNaN(mi) || mi < 0 || mi >= 60 || hi < 0) return null;
    return hi * 60 + mi;
  }
  // Plain number → assume minutes
  const n = parseInt(s, 10);
  if (Number.isNaN(n) || n < 0) return null;
  return n;
}

export function HHMMInput({
  value,
  onChange,
  placeholder = "0:00",
  disabled,
  className = "",
  max,
  ...rest
}) {
  const [text, setText] = useState(() => minutesToHHMM(value));
  const [touched, setTouched] = useState(false);

  // Keep in sync when parent updates value externally — but only if our text
  // currently reflects the parent value (i.e. not while user is typing/invalid).
  useEffect(() => {
    const expected = minutesToHHMM(value);
    if (expected !== text && hhmmToMinutes(text) === value) {
      // text already maps to the same minutes, keep it
      return;
    }
    if (hhmmToMinutes(text) == null && text) {
      // Don't clobber an invalid-but-unsubmitted value
      return;
    }
    setText(expected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = () => {
    setTouched(true);
    if (!text || !text.trim()) {
      // Empty → propagate null but no error
      onChange(null);
      return;
    }
    const mins = hhmmToMinutes(text);
    if (mins == null) {
      // Invalid input — DO NOT reset text; let the user see what they typed
      // and the red border. Parent value is not updated (stays whatever it was).
      return;
    }
    if (typeof max === "number" && mins > max) {
      onChange(max);
      setText(minutesToHHMM(max));
      return;
    }
    onChange(mins);
    setText(minutesToHHMM(mins));  // normalize "75" → "1:15"
  };

  const invalid = touched && !!text && hhmmToMinutes(text) == null;

  return (
    <Input
      {...rest}
      type="text"
      inputMode="numeric"
      placeholder={placeholder}
      disabled={disabled}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
      className={`${className} ${invalid ? "border-red-500" : ""}`}
    />
  );
}

export default HHMMInput;
